import React, { useState, useMemo, useCallback } from 'react';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  useApiClient, ComboBoxOption, useFetchImmutable, ZSpinner,
} from '@marageti/z4-lib';
import { Box, Container } from '@mui/material';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { Destination } from '@marageti/z4-sdk/lib/travel';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useSnackbar } from 'notistack';
import DestinationsView from './destinations-view';
import { StatusFilter } from './types';

const addOrUpdateDestination = (
  destinations: Destination[],
  newDestination: Destination,
): Destination[] => {
  const updated = [...destinations];
  const existingIndex = updated.findIndex((e) => e.id === newDestination.id);
  if (existingIndex >= 0) {
    updated[existingIndex] = newDestination;
    return updated;
  }
  updated.push(newDestination);
  return updated;
};

const formatCountryOptions = (obj: CountryCodeMap | undefined): ComboBoxOption[] => {
  if (!obj) {
    return [{ label: 'Loading...', value: '' }];
  }
  const ret: ComboBoxOption[] = [];
  Object.keys(obj).forEach((key) => {
    const opt = {
      label: obj[key],
      value: key,
    };
    ret.push(opt);
  });
  return ret.sort((a, b) => a.label.localeCompare(b.label));
};

const sortDestinations = (destinations: Destination[]): Destination[] => destinations.sort((a, b) => a.name.localeCompare(b.name));

const DestinationsContainer = () => {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(
    apiClient.standardsClient.getCountryCodes,
    [],
    true,
  );

  const {
    data: destinations,
    error,
    mutate,
  } = useFetchImmutable<Destination[]>(
    apiClient.destinationsClient.getDestinations,
    [{ params: { all: true, limit: 9000 } }],
    true,
  );

  if (error?.code === 401) navigate('/logout');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm();

  const [showModal, setShowModal] = useState(false);
  const [editorState, setEditorState] = useState<Partial<Destination> | null>(null);
  const [submissionError, setSubmissionError] = useState<ApiError>();

  // Filter states
  const [nameFilter, setNameFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState<ComboBoxOption | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const countryOptions = useMemo(() => formatCountryOptions(countryCodes), [countryCodes]);

  const destinationsList = useMemo(() => {
    let filtered = destinations || [];

    // Filter by name (case-insensitive partial match)
    if (nameFilter) {
      const searchTerm = nameFilter.toLowerCase();
      filtered = filtered.filter((d) => d.name.toLowerCase().includes(searchTerm));
    }

    // Filter by country
    if (countryFilter) {
      filtered = filtered.filter((d) => d.countries.includes(countryFilter.value));
    }

    // Filter by status
    if (statusFilter === 'active') {
      filtered = filtered.filter((d) => d.served);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter((d) => !d.served);
    }

    return sortDestinations(filtered);
  }, [destinations, nameFilter, countryFilter, statusFilter]);

  const onChangeNameFilter = useCallback((value: string) => {
    setNameFilter(value);
  }, []);

  const onChangeCountryFilter = useCallback((value: ComboBoxOption | null) => {
    setCountryFilter(value);
  }, []);

  const onChangeStatusFilter = useCallback((value: StatusFilter) => {
    setStatusFilter(value);
  }, []);

  const setFormValues = (destination: Destination) => {
    setValue('name', destination.name || '');
    setValue('served', destination.served ?? true);
    // Convert country codes to ComboBoxOption format
    const countryValues = destination.countries.map((code) => ({
      label: countryCodes?.[code] || code,
      value: code,
    }));
    setValue('countries', countryValues);
  };

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    const isUpdate = !!editorState?.id;

    // Extract country codes from ComboBoxOption array
    const countryCodesArray = (data.countries as ComboBoxOption[]).map(
      (option) => option.value,
    );

    const destinationData = {
      name: data.name,
      countries: countryCodesArray,
      served: data.served ?? true,
    };

    let prom;
    if (isUpdate) {
      const updateData: Destination = {
        ...editorState as Destination,
        ...destinationData,
      };
      prom = apiClient.destinationsClient.putDestination(editorState.id as string, updateData);
    } else {
      prom = apiClient.destinationsClient.postDestination(destinationData);
    }

    prom
      .then((res) => {
        setShowModal(false);
        setEditorState(null);
        reset({
          name: '',
          countries: [],
          served: true,
        });
        mutate(addOrUpdateDestination(destinations || [], res));
        enqueueSnackbar(
          isUpdate ? 'Destination updated.' : 'Destination created.',
          { variant: 'success' },
        );
      })
      .catch((e: ApiError) => {
        if (e.code === 401) navigate('/logout');
        setSubmissionError(e);
      });
  };

  const onClickEdit = (destination: Destination) => {
    setEditorState(destination);
    setFormValues(destination);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditorState(null);
    setSubmissionError(undefined);
    reset({
      name: '',
      countries: [],
      served: true,
    });
  };

  const onClickCreate = () => {
    setShowModal(true);
    setEditorState(null);
    reset({
      name: '',
      countries: [],
      served: true,
    });
  };

  if (!destinations) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" py={8}>
          <ZSpinner />
        </Box>
      </Container>
    );
  }

  return (
    <DestinationsView
      control={control}
      destinationsList={destinationsList}
      countryOptions={countryOptions}
      editorState={editorState}
      error={error}
      errors={errors}
      filters={{ nameFilter, countryFilter, statusFilter }}
      onChangeNameFilter={onChangeNameFilter}
      onChangeCountryFilter={onChangeCountryFilter}
      onChangeStatusFilter={onChangeStatusFilter}
      handleSubmit={handleSubmit}
      onClickCreate={onClickCreate}
      onClickEdit={onClickEdit}
      onClose={closeModal}
      onSubmit={onSubmit}
      register={register}
      showModal={showModal}
      submissionError={submissionError}
    />
  );
};

export default DestinationsContainer;
